import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Plus, Landmark, Store, ArrowUpDown } from "lucide-react";
import { NewDealModal } from "@/pages/Pipeline";
import { DueChip } from "@/components/DueChip";

const STAGE_BADGE = {
  Opportunity: "bg-zinc-100 text-zinc-600", Pursuing: "bg-blue-100 text-blue-700",
  Waiting: "bg-amber-100 text-amber-700", Submitted: "bg-cyan-100 text-cyan-700",
  Won: "bg-emerald-100 text-emerald-700", Lost: "bg-red-100 text-red-700", Passed: "bg-zinc-200 text-zinc-500",
};
const SOL_TYPES = ["All", "IFB", "RFP", "RFQ", "Task Order", "Unknown"];
const readColor = (p) => p >= 90 ? "text-emerald-600" : p >= 50 ? "text-amber-600" : "text-red-600";

export default function Proposals({ type }) {
  const [deals, setDeals] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [solFilter, setSolFilter] = useState("All");
  const [sort, setSort] = useState({ key: "due_date", dir: 1 });
  const navigate = useNavigate();
  const Icon = type === "Federal" ? Landmark : Store;
  const isCommercial = type === "Commercial";

  const load = () => api.get("/bids").then((r) => setDeals((r.data || []).filter((d) => d.bid_type === type))).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [type]);

  const rows = useMemo(() => {
    const effectiveFilter = isCommercial ? "RFQ" : solFilter;
    let r = deals.filter((d) => effectiveFilter === "All" || (d.solicitation_type || "Unknown") === effectiveFilter);
    const { key, dir } = sort;
    return [...r].sort((a, b) => {
      if (key === "readiness_pct") return ((a.readiness_pct || 0) - (b.readiness_pct || 0)) * dir;
      const va = (a[key] ?? "").toString().toLowerCase(), vb = (b[key] ?? "").toString().toLowerCase();
      return va < vb ? -dir : va > vb ? dir : 0;
    });
  }, [deals, solFilter, sort, isCommercial]);

  const th = (label, key) => (
    <th onClick={() => setSort((s) => ({ key, dir: s.key === key ? -s.dir : 1 }))}
      data-testid={`sort-${key}`} className="px-3 py-2 text-left font-semibold cursor-pointer select-none hover:text-zinc-900">
      <span className="inline-flex items-center gap-1">{label}<ArrowUpDown className="h-3 w-3 text-zinc-300" /></span>
    </th>
  );

  return (
    <div className="p-5 sm:p-8 max-w-[1700px] mx-auto" data-testid={`proposals-${type.toLowerCase()}`}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-zinc-900 flex items-center gap-2.5">
            <Icon className="h-7 w-7" /> {type} Proposals
          </h1>
          <p className="text-sm text-zinc-500 mt-1">{type === "Federal" ? "Federal solicitations, run by Anvil through the 7+7B gate." : "Commercial bids with tracked WD proposal numbers."}</p>
        </div>
        <button data-testid="proposals-new-btn" onClick={() => setShowNew(true)}
          className="flex items-center gap-2 bg-zinc-900 text-white font-semibold text-sm px-4 py-2.5 rounded-md hover:bg-zinc-800"><Plus className="h-4 w-4" /> New {type.toLowerCase()} deal</button>
      </div>

      <div className="flex gap-1.5 mb-4 flex-wrap" data-testid="soltype-filter">
        {isCommercial ? (
          <span data-testid="soltype-rfq-only" className="text-xs px-3 py-1.5 rounded-full border bg-zinc-900 text-white border-zinc-900">RFQ</span>
        ) : (
          SOL_TYPES.map((t) => (
            <button key={t} data-testid={`soltype-${t.replace(/\s/g, "").toLowerCase()}`} onClick={() => setSolFilter(t)}
              className={`text-xs px-3 py-1.5 rounded-full border ${solFilter === t ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>{t}</button>
          ))
        )}
      </div>

      <div className="hidden md:block bg-white border border-zinc-200 shadow-sm rounded-xl overflow-hidden" data-testid="proposals-list">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col className="w-[19%]" /><col className="w-[17%]" /><col className="w-[13%]" />
            <col className="w-[9%]" /><col className="w-[9%]" /><col className="w-[7%]" />
            <col className="w-[8%]" /><col className="w-[9%]" /><col className="w-[9%]" />
          </colgroup>
          <thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500">
            <tr>{th("Name", "title")}{th("Agency", "agency")}{th("Code", "est_code")}{th("Due", "due_date")}{th("Amount", "amount")}{th("Type", "solicitation_type")}{th("Readiness", "readiness_pct")}{th("Stage", "pipeline_stage")}{th("Owner", "owner_name")}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-xs text-zinc-400">No {type} deals match this filter.</td></tr>}
            {rows.map((d) => (
              <tr key={d.id} data-testid={`proposal-row-${d.id}`} onClick={() => navigate(`/deals/${d.id}`)}
                className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer align-top">
                <td className="px-3 py-2.5 text-zinc-900 font-medium break-words [overflow-wrap:anywhere]">{d.title}</td>
                <td className="px-3 py-2.5 text-zinc-600 break-words [overflow-wrap:anywhere] line-clamp-3" title={d.agency || ""}>{d.agency || "—"}</td>
                <td className="px-3 py-2.5 text-zinc-500 font-mono text-[11px] break-words [overflow-wrap:anywhere]">{type === "Commercial" ? (d.commercial_proposal_number || d.est_code) : (d.solicitation_number || d.est_code)}</td>
                <td className="px-3 py-2.5 text-zinc-600 break-words">{d.due_date || "—"}<div className="mt-1"><DueChip due={d.due_date} testid={`due-chip-${d.id}`} /></div></td>
                <td className="px-3 py-2.5 text-zinc-900 font-semibold break-words">{d.amount || "—"}</td>
                <td className="px-3 py-2.5"><span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600 break-words" title={d.solicitation_type_reason || ""}>{d.solicitation_type || "—"}</span></td>
                <td className={`px-3 py-2.5 font-semibold ${readColor(d.readiness_pct || 0)}`} data-testid={`readiness-${d.id}`}>{d.readiness_pct != null ? `${d.readiness_pct}%` : "—"}</td>
                <td className="px-3 py-2.5"><span className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-semibold ${STAGE_BADGE[d.pipeline_stage || "Opportunity"]}`}>{d.pipeline_stage || "Opportunity"}</span></td>
                <td className="px-3 py-2.5 text-zinc-600 break-words [overflow-wrap:anywhere]">{d.estimator || d.owner_name || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-2.5" data-testid="proposals-list-mobile">
        {rows.length === 0 && <div className="text-center text-xs text-zinc-400 py-8 bg-white border border-zinc-200 rounded-xl">No {type} deals match this filter.</div>}
        {rows.map((d) => (
          <div key={d.id} data-testid={`proposal-card-${d.id}`} onClick={() => navigate(`/deals/${d.id}`)}
            className="bg-white border border-zinc-200 shadow-sm rounded-xl p-4 cursor-pointer active:bg-zinc-50">
            <div className="flex items-start justify-between gap-2">
              <div className="font-semibold text-zinc-900 text-sm break-words [overflow-wrap:anywhere]">{d.title}</div>
              <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full font-semibold ${STAGE_BADGE[d.pipeline_stage || "Opportunity"]}`}>{d.pipeline_stage || "Opportunity"}</span>
            </div>
            {d.agency && <div className="text-xs text-zinc-500 mt-1 break-words [overflow-wrap:anywhere]">{d.agency}</div>}
            <div className="text-[11px] font-mono text-zinc-500 mt-1.5 break-words [overflow-wrap:anywhere]">{type === "Commercial" ? (d.commercial_proposal_number || d.est_code) : (d.solicitation_number || d.est_code)}</div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2.5 text-xs">
              <span className="text-zinc-500">Due: <span className="text-zinc-700">{d.due_date || "—"}</span></span>
              <DueChip due={d.due_date} testid={`due-chip-card-${d.id}`} />
              <span className="text-zinc-500">Amount: <span className="text-zinc-900 font-semibold">{d.amount || "—"}</span></span>
              <span className="text-zinc-500">Type: <span className="text-zinc-700">{d.solicitation_type || "—"}</span></span>
              <span className="text-zinc-500">Readiness: <span className={`font-semibold ${readColor(d.readiness_pct || 0)}`}>{d.readiness_pct != null ? `${d.readiness_pct}%` : "—"}</span></span>
            </div>
            <div className="text-xs text-zinc-500 mt-1.5 break-words [overflow-wrap:anywhere]">Owner: <span className="text-zinc-700">{d.estimator || d.owner_name || "—"}</span></div>
          </div>
        ))}
      </div>

      {showNew && <NewDealModal lockType={type} onClose={() => setShowNew(false)} onCreated={(d) => { setShowNew(false); navigate(`/deals/${d.id}`); }} />}
    </div>
  );
}
